import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { ORDERS_QUERY } from "../../suspensions-and-terminations/queries";
import { Variables } from "../types";
import EmptyState from "@/components/EmptyState";
import Table, { TableDetails, type Row } from "@/components/Table";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import { renderStatus } from "@/pages/billing/_utils";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useNavigate, Path } from "@/router";
import usePagination from "@/hooks/usePagination";
import { Controller, useForm } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import { reject, isEmpty } from "lodash";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import { NumberFormatBase } from "react-number-format";
import TextField from "@/components/TextField";
import { isNull, get } from "lodash";
import FacilityAutocomplete from "../../../_components/FacilityAutocomplete";
import { FormProvider } from "react-hook-form";
import { ExpandedFiltersWrapper } from "@/components/ExpandedFilters";
import SectionAutocomplete from "../../../_components/SectionAutocomplete";
import { currencyFormatter } from "@/lib/currencyHelpers";
import { DateValueType } from "react-tailwindcss-datepicker";
import { useSearch } from "@/hooks/useSearch";
import { GET_CLASSIFICATIONS } from "../../../queries";
import MetricGrid from "@/components/MetricGrid";
import { useParams } from "@/hooks/useSearchParams";
import { ClassificationType, StatsType } from "../../types";
import { OrderRow, OrderNode } from "@/pages/billing/types";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { buildColumns } from "@/pages/billing/_utils";

export type FormValues = {
  name: string;
  minBalance: string;
  maxBalance: string;
  memberClassifications: string[];
  paymentStatuses: string[];
  sectionIds: string[];
  facilityIds: string[];
  eligibleForSuspensionOn: DateValueType | null;
  eligibleForTerminationOn: DateValueType | null;
  facility: string;
  section: string;
};

const mapCommerceOrders = (data: OrderNode[]) =>
  (data || []).map(({ updatedAt, dueDate, balance, lineItems, ...order }) => ({
    ...order,
    pgaId: order.person.pgaId,
    classification: order.person.classification || "",
    section: order.person.primarySectionAffiliation?.section.name || "",
    updatedAt: formatDate(updatedAt),
    dueDate: formatDate(dueDate),
    balance: centsToDollarsFormatted(balance) || undefined,
    products: lineItems.length
  }));

const formatDate = (date?: string): string | undefined => (date && toFormat(date)) || undefined;

export const OrdersTable = ({ defaultVariables }: { defaultVariables: Variables }) => {
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  let { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  params.balanceRange = params.maxBalance && { to: Number(params.maxBalance) };
  params.balanceRange = params.minBalance && { from: Number(params.minBalance) };
  const { renderFooter, paginationVariables, resetPagination } = usePagination();

  const { searchValue, control: controlSearch, setValue: setSearchValue } = useSearch("name", params.name || "");

  const [selectedValues, setSelectedValues] = useState<string[]>(params.paymentStatuses || []);
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>(
    params.memberClassifications || []
  );

  const [variables, setVariables] = useState<Variables>({ ...defaultVariables, ...params });
  const { data, loading } = useQuery(ORDERS_QUERY, { variables: { ...variables, ...paginationVariables } });
  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);

  const navigate = useNavigate();

  const form = useForm<FormValues>();
  const { control, reset, watch, getValues } = form;

  const pattern = "/billing/orders";
  function onClickRow(row: Row<OrderRow>) {
    navigate(
      `${pattern}/${row.original.id}?return_to=/billing/dues-management/reporting/unpaid-dues&${searchParamsUrl}` as Path
    );
  }

  const orderData: OrderRow[] = mapCommerceOrders(data?.duesOrders?.nodes || []);

  const [stats, setStats] = useState<StatsType>({
    totalCount: null,
    failedPayments: null,
    overdueOrders: null
  });

  useEffect(() => {
    if (!loading && data) {
      if (stats.totalCount === null || stats.failedPayments == null) {
        setStats({
          totalCount: data.duesOrders?.totalCount,
          failedPayments: data.totalDuesOrdersWithFailedPayments?.totalCount,
          overdueOrders: data.totalDuesOverdueOrders?.totalCount
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);
  const sectionIds = watch("sectionIds");
  const facilityIds = watch("facilityIds");

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      ...(sectionIds ? { sectionIds: sectionIds } : {}),
      ...(facilityIds ? { facilityIds: facilityIds } : {})
    }));
  }, [sectionIds, facilityIds]);

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      query: searchValue
    }));
  }, [searchValue, setVariables]);

  const { isCompactLayout } = useIsCompactLayout();

  const filters = {
    eligibleForSuspensionOn: !!variables.eligibleForSuspensionOn,
    paymentStatuses: selectedValues.filter((v) => v !== "OPEN").length > 0,
    clasifications: selectedClassificationValues.length > 0,
    minBalance: !!getValues("minBalance"),
    maxBalance: !!getValues("maxBalance"),
    eligibleForTerminationOn: !!variables.eligibleForTerminationOn,
    section: params.sectionIds?.length > 0,
    facility: params.facilityIds?.length > 0
  };

  const classificationNodes = get(classificationData, "classifications.nodes");
  const classificationDataNodes =
    classificationNodes?.map((node: ClassificationType) => ({
      value: node.name,
      label: node.name
    })) || [];

  const onClearAll = () => {
    setVariables(defaultVariables);
    setSelectedValues([]);
    setSelectedClassificationValues([]);
    reset({
      eligibleForSuspensionOn: { startDate: null, endDate: null },
      eligibleForTerminationOn: { startDate: null, endDate: null }
    });
    deleteSearchParams([
      "eligibleForSuspensionOn",
      "eligibleForTerminationOn",
      "maxBalence",
      "minBalance",
      "memberClassifications[]",
      "paymentStatuses[]",
      "facilityIds[]",
      "sectionIds[]",
      "facility",
      "section"
    ]);
  };
  return (
    <>
      {stats.totalCount === 0 ? (
        <EmptyState title="No Unpaid Dues Orders" iconName="person" />
      ) : (
        <>
          <MetricGrid
            metrics={[
              {
                label: `Total Unpaid Dues`,
                value: `${!isNull(stats.totalCount) && stats.totalCount}`,
                loading: isNull(stats.totalCount),
                iconName: "receipt_long"
              },
              {
                label: `Orders in Active Filter(s)`,
                value: `${data?.duesOrders?.totalCount}`,
                loading: loading,
                iconName: "receipt_long"
              },
              {
                label: `Overdue Orders`,
                value: `${!isNull(stats.overdueOrders) && stats.overdueOrders}`,
                loading: isNull(stats.overdueOrders),
                variant:
                  stats.overdueOrders !== null && stats.overdueOrders !== undefined && stats.overdueOrders > 0
                    ? "error"
                    : undefined,
                iconName: "receipt_long"
              },
              {
                label: `Total Orders with Failed Payment`,
                value: `${!isNull(stats.failedPayments) && stats.failedPayments}`,
                loading: isNull(stats.failedPayments),
                variant:
                  stats.failedPayments !== null && stats.failedPayments !== undefined && stats.failedPayments > 0
                    ? "error"
                    : undefined,
                iconName: "receipt_long"
              }
            ]}
            layout="stacked"
          />
          <div className="w-full">
            <Table
              data={orderData || []}
              loading={loading}
              onClickRow={onClickRow}
              renderDetails={() => {
                const bodyMessage =
                  "List of Dues orders that have been billed but not fully paid. When open orders become overdue, they will be added to the “Eligible for Suspension” list.";
                return <TableDetails heading="Unpaid Dues Orders" body={bodyMessage} />;
              }}
              renderHeader={() => (
                <div>
                  <div className="flex md:items-center justify-between">
                    <div className="flex md:items-center gap-3 flex-row w-full">
                      <FormProvider {...form}>
                        <div className="w-full sm:max-w-[320px]">
                          <Controller
                            name="name"
                            control={controlSearch}
                            render={({ field }) => (
                              <SearchInput
                                placeholder="Search Orders"
                                query={field.value}
                                onChange={(e) => {
                                  field.onChange(e);
                                  resetPagination();
                                  addSearchParam("name", e);
                                }}
                                onClear={() => {
                                  deleteSearchParams("name");
                                  setSearchValue("query", "");
                                }}
                              />
                            )}
                          />
                        </div>
                        <ExpandedFiltersWrapper
                          filters={filters}
                          items={[
                            {
                              component: (
                                <Controller
                                  name="eligibleForSuspensionOn"
                                  control={control}
                                  render={({ field }) => {
                                    return (
                                      <DateSelect
                                        placeholder="Select a Date"
                                        label="Eligible for Suspension On"
                                        useModal={true}
                                        value={field.value}
                                        useSingleDate
                                        onChange={(e) => {
                                          field.onChange(e);
                                          if (typeof e === "object" && e !== null) {
                                            setVariables((prevValue) => ({
                                              ...prevValue,
                                              eligibleForSuspensionOn: e.endDate
                                            }));
                                            addSearchParam("eligibleForSuspensionOn", e.endDate?.toString() || "");
                                          }
                                        }}
                                      />
                                    );
                                  }}
                                />
                              )
                            },
                            {
                              component: (
                                <FacilityAutocomplete
                                  sectionIds={variables?.sectionIds}
                                  addSearchParam={addSearchParam}
                                  deleteSearchParams={deleteSearchParams}
                                  params={params}
                                  name="facility"
                                />
                              )
                            },
                            {
                              component: (
                                <Controller
                                  name="paymentStatuses"
                                  control={control}
                                  render={() => (
                                    <MultiSelect
                                      label="Status"
                                      selectedValues={selectedValues}
                                      options={[
                                        { value: "HAS_FAILED_PAYMENTS", label: "Has Failed Payment" },
                                        { value: "HAS_REFUNDED_PAYMENTS", label: "Has Refunded Payment" },
                                        { value: "IS_OVERDUE", label: "Overdue" }
                                      ]}
                                      onSelect={(value, checked) => {
                                        let status = checked
                                          ? [...selectedValues, value]
                                          : selectedValues.filter((v) => v !== value);

                                        setSelectedValues(reject(status, isEmpty));
                                        setVariables((prevValue) => ({
                                          ...prevValue,
                                          paymentStatuses: reject(status, isEmpty)
                                        }));
                                        addSearchParam("paymentStatuses[]", reject(status, isEmpty).join(","));
                                      }}
                                      onClear={() => {
                                        setSelectedValues([]);
                                        setVariables((prevValue) => ({
                                          ...prevValue,
                                          paymentStatuses: []
                                        }));
                                        deleteSearchParams("paymentStatuses[]");
                                      }}
                                      className="w-full"
                                    />
                                  )}
                                />
                              )
                            },
                            {
                              component: (
                                <Controller
                                  name="memberClassifications"
                                  control={control}
                                  render={() => (
                                    <MultiSelect
                                      label="Classification"
                                      selectedValues={selectedClassificationValues}
                                      options={classificationDataNodes}
                                      onSelect={(value, checked) => {
                                        const status = checked
                                          ? [...selectedClassificationValues, value]
                                          : selectedClassificationValues.filter((v) => v !== value);
                                        setSelectedClassificationValues(reject(status, isEmpty));
                                        setVariables((prevValue) => ({
                                          ...prevValue,
                                          memberClassifications: reject(status, isEmpty)
                                        }));
                                        addSearchParam("memberClassifications[]", reject(status, isEmpty).join(","));
                                      }}
                                      onClear={() => {
                                        setSelectedClassificationValues([]);
                                        setVariables((prevValue) => ({
                                          ...prevValue,
                                          memberClassifications: []
                                        }));
                                        deleteSearchParams("memberClassifications[]");
                                      }}
                                      className="w-full"
                                    />
                                  )}
                                />
                              )
                            },
                            {
                              component: (
                                <Controller
                                  name="minBalance"
                                  control={control}
                                  defaultValue={params.minBalance || ""}
                                  render={({ field }) => {
                                    return (
                                      <NumberFormatBase
                                        label="Min Balance"
                                        placeholder="Enter Amount"
                                        leadingIcon="attach_money"
                                        customInput={TextField}
                                        format={currencyFormatter}
                                        value={field.value ?? ""}
                                        name={field.name}
                                        onValueChange={(values) => {
                                          field.onChange(values.floatValue);
                                          if (values.floatValue) {
                                            setVariables((prevValue) => ({
                                              ...prevValue,
                                              balanceRange: {
                                                from: values.floatValue,
                                                to: prevValue.balanceRange?.to
                                              }
                                            }));
                                            addSearchParam("minBalance", values.floatValue.toString());
                                          } else {
                                            setVariables((prevValue) => {
                                              const { balanceRange, ...rest } = prevValue;
                                              const { to } = balanceRange || {};
                                              return {
                                                balanceRange: {
                                                  to
                                                },
                                                ...rest
                                              };
                                            });
                                            deleteSearchParams("minBalance");
                                          }
                                        }}
                                        className="w-full"
                                      />
                                    );
                                  }}
                                />
                              )
                            },
                            {
                              component: (
                                <Controller
                                  name="maxBalance"
                                  control={control}
                                  defaultValue={params.maxBalance || ""}
                                  render={({ field }) => {
                                    return (
                                      <NumberFormatBase
                                        label="Max Balance"
                                        placeholder="Enter Amount"
                                        leadingIcon="attach_money"
                                        customInput={TextField}
                                        format={currencyFormatter}
                                        value={field.value ?? ""}
                                        name={field.name}
                                        onValueChange={(values) => {
                                          field.onChange(values.floatValue);
                                          if (values.floatValue) {
                                            setVariables((prevValue) => ({
                                              ...prevValue,
                                              balanceRange: {
                                                from: prevValue.balanceRange?.from,
                                                to: values.floatValue
                                              }
                                            }));
                                            addSearchParam("maxBalance", values.floatValue.toString());
                                          } else {
                                            setVariables((prevValue) => {
                                              const { balanceRange, ...rest } = prevValue;
                                              const { from } = balanceRange || {};
                                              return {
                                                balanceRange: {
                                                  from
                                                },
                                                ...rest
                                              };
                                            });
                                            deleteSearchParams("maxBalance");
                                          }
                                        }}
                                        className="w-full"
                                      />
                                    );
                                  }}
                                />
                              )
                            },
                            {
                              component: (
                                <SectionAutocomplete
                                  addSearchParam={addSearchParam}
                                  deleteSearchParams={deleteSearchParams}
                                  params={params}
                                  name="section"
                                />
                              )
                            },

                            {
                              component: (
                                <Controller
                                  name="eligibleForTerminationOn"
                                  control={control}
                                  render={({ field }) => {
                                    return (
                                      <DateSelect
                                        placeholder="Select a Date"
                                        label="Eligible for Termination On"
                                        useModal={true}
                                        value={
                                          params.eligibleForTerminationOn
                                            ? {
                                                startDate: new Date(params.eligibleForTerminationOn),
                                                endDate: new Date(params.eligibleForTerminationOn)
                                              }
                                            : field.value
                                        }
                                        useSingleDate
                                        onChange={(e) => {
                                          field.onChange(e);
                                          if (typeof e === "object" && e !== null) {
                                            setVariables((prevValue) => ({
                                              ...prevValue,
                                              eligibleForTerminationOn: e.endDate
                                            }));
                                            addSearchParam("eligibleForTerminationOn", e.endDate?.toString() || "");
                                          }
                                        }}
                                        supportingText="Orders 30+ days overdue on this date"
                                      />
                                    );
                                  }}
                                />
                              )
                            }
                          ]}
                          onClearAll={onClearAll}
                        />
                      </FormProvider>
                    </div>
                    <div className="hidden ml-3 lg:block">
                      {renderFooter(data?.duesOrders, {
                        totalCount: data?.duesOrders?.totalCount || 0,
                        pageCount: orderData.length,
                        variant: "compact"
                      })()}
                    </div>
                  </div>

                  <div className="block mt-3 lg:hidden">
                    {renderFooter(data?.duesOrders, {
                      totalCount: data?.duesOrders?.totalCount || 0,
                      pageCount: orderData.length,
                      variant: "compact"
                    })()}
                  </div>
                </div>
              )}
              columns={[
                ...buildColumns<OrderRow>({
                  isCompactLayout,
                  hasCheckbox: false
                }),
                { accessorKey: "pgaId", header: "PGA ID", enableSorting: false },
                { accessorKey: "classification", header: "Classification", enableSorting: false },
                { accessorKey: "section", header: "Section", enableSorting: false },
                {
                  accessorKey: "products",
                  header: "Products",
                  enableSorting: false
                },
                { accessorKey: "updatedAt", header: "Modified", enableSorting: false },
                { accessorKey: "dueDate", header: "Due Date", enableSorting: false },
                { accessorKey: "balance", header: "Balance", enableSorting: false },
                {
                  header: "Stage",
                  id: "stage",
                  cell: ({ row: { original } }) => renderStatus(original.status, original.paymentStatus),
                  size: 100
                }
              ]}
              renderFilteredEmptyState={() => (
                <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="person" />
              )}
              renderFooter={() => (
                <div className={`justify-end flex flex-col md:flex-row md:items-center`}>
                  {renderFooter(data?.duesOrders, {
                    totalCount: data?.duesOrders?.totalCount || 0,
                    pageCount: orderData.length
                  })()}
                </div>
              )}
            />
          </div>
        </>
      )}
    </>
  );
};

export default OrdersTable;
